/**
 * Native Google sign-in — the OS account picker, not a redirect.
 *
 * WHY THIS EXISTS. The web client signs in with Google by *navigating* to `/auth/google`, which
 * 302s to Google and comes back to `WEB_APP_URL` with a session. That cannot work in the bundled
 * Capacitor shell, and not merely as an inconvenience: Google refuses OAuth inside an embedded
 * WebView outright (`disallowed_useragent`), so there is nothing for the anchor to reach. The
 * shell therefore asks Android for a Google `id_token` through the OS account picker and posts
 * that to `auth.googleNative`, which trades it for the same session the callback would have
 * created — same verification, same account rules, same `user_sessions` row.
 *
 * WHAT IS THE SAME. Only the way the assertion is obtained. `signInWithGoogleIdToken` on the
 * server is the single place either path resolves an account and mints a session, so there is no
 * second set of rules about unverified addresses or guests to drift from.
 *
 * THE ERROR TAXONOMY IS THE POINT OF THIS FILE. A dismissed picker is not a failure and must
 * never be shown as one; a picker that succeeded and returned no token is a configuration bug
 * that has to say so; and neither may be confused with the server refusing the exchange. Trade
 * Loop's `nativeGoogleAuth.ts` carries a fourth code, `NONCE_UNSUPPORTED`, because Supabase's
 * exchange demands a hashed nonce — chompdome's own endpoint does not, and a nonce this client
 * both chooses and echoes back would prove nothing to it (see the `googleNative` procedure), so
 * there is no nonce here and no code for one.
 *
 * IOS IS A SEPARATE CARD and this file is deliberately Android-only today: `initialize` passes
 * no `iOSClientId`/`iOSServerClientId`, without which the plugin cannot sign in there at all.
 * When that card lands it is those two values plus `forcePrompt: true` on the login call —
 * without the latter the plugin takes its `restorePreviousSignIn()` shortcut and returns a
 * *cached* `id_token`, which is the classic "works on the device, refused by the backend" bug.
 */

import { SocialLogin } from '@capgo/capacitor-social-login';

/** How a native sign-in can fail before the server has been asked anything. */
export type NativeGoogleErrorCode =
  /** The player dismissed the account picker. Never surface this as an error. */
  | 'USER_CANCELLED'
  /** The picker succeeded and returned no `id_token` — there is nothing to exchange. */
  | 'NO_ID_TOKEN'
  /** Anything else the plugin threw: a misconfigured client id, no Play Services, no network. */
  | 'PLUGIN_ERROR';

export class NativeGoogleAuthError extends Error {
  constructor(
    message: string,
    readonly code: NativeGoogleErrorCode,
    readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'NativeGoogleAuthError';
  }
}

/**
 * `initialize` is idempotent for our purposes but not free, so it runs once per app session —
 * and is re-run if the client id changes, which only a `me` answering differently can do. A
 * failure clears the memo so the next press can retry rather than inheriting it forever.
 */
let initialized: { webClientId: string; done: Promise<void> } | null = null;

function initialize(webClientId: string): Promise<void> {
  if (initialized?.webClientId !== webClientId) {
    const done = SocialLogin.initialize({
      google: {
        // Android's `serverClientId`, and therefore the `aud` claim of the `id_token` it mints.
        // It is the WEB client id — the Android OAuth client exists only so Google will honour
        // this app's package name and signing certificate, and is never named here. The server
        // verifies against the same `GOOGLE_CLIENT_ID`, which is why this value is served by
        // `auth.me` rather than baked into the bundle.
        webClientId,
        // 'online' returns the id_token. 'offline' returns only a serverAuthCode, which
        // `auth.googleNative` does not accept.
        mode: 'online',
      },
    }).catch((error: unknown) => {
      initialized = null;
      throw error;
    });
    initialized = { webClientId, done };
  }
  return initialized.done;
}

/** Reset the one-shot initialize memo. Test seam only. */
export function resetGoogleSignInInitialization(): void {
  initialized = null;
}

/**
 * The plugin rejects with `code === 'USER_CANCELLED'` on dismissal. The message check beside it
 * is belt and braces, and the asymmetry is deliberate: mistaking a real failure for a cancel
 * costs one missing message, while mistaking a cancel for a failure shows the player an error
 * they did not cause and did not ask about.
 */
function isUserCancellation(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  if (code === 'USER_CANCELLED') return true;
  const message = (error as { message?: unknown } | null)?.message;
  const text = typeof message === 'string' ? message.toLowerCase() : '';
  return text.includes('cancel') || text.includes('access_denied');
}

/**
 * Open the OS account picker and return the Google `id_token` to exchange.
 *
 * Nothing is passed but the provider: the default `style: 'standard'` is the full account picker
 * (the web path's own `prompt=select_account`, which is what a shared phone needs), and the
 * default scopes are the three the server asks for. Every option this call does not send is one
 * the server would have had to trust.
 */
export async function requestGoogleIdToken(webClientId: string): Promise<string> {
  try {
    await initialize(webClientId);
  } catch (error) {
    throw new NativeGoogleAuthError(
      'Google sign-in could not start on this device.',
      'PLUGIN_ERROR',
      error,
    );
  }

  let response: Awaited<ReturnType<typeof SocialLogin.login<'google'>>>;
  try {
    response = await SocialLogin.login({ provider: 'google', options: {} });
  } catch (error) {
    if (isUserCancellation(error)) {
      throw new NativeGoogleAuthError('Google sign-in was cancelled.', 'USER_CANCELLED', error);
    }
    const message = (error as { message?: unknown } | null)?.message;
    throw new NativeGoogleAuthError(
      typeof message === 'string' && message !== '' ? message : 'Google sign-in failed.',
      'PLUGIN_ERROR',
      error,
    );
  }

  const result = response.result;
  const idToken = result.responseType === 'online' ? result.idToken : null;
  if (idToken === null || idToken === '') {
    // Reached when the plugin is initialised in 'offline' mode, or when the account picker
    // returned a credential carrying no identity token. Either way there is nothing to post.
    throw new NativeGoogleAuthError(
      'Google did not return an identity token.',
      'NO_ID_TOKEN',
      response,
    );
  }

  return idToken;
}

/**
 * What to put on screen after a failed native sign-in, or `null` to say nothing at all.
 *
 * A pure function of the thrown error, so the button renders an outcome instead of deciding one,
 * and the mapping is testable without a device. `null` for a cancellation is the whole reason it
 * returns a nullable string rather than a message: dismissing the picker is a decision the
 * player made, not something that went wrong.
 *
 * Anything that is not a `NativeGoogleAuthError` came back from `auth.googleNative`, and the
 * server already answers those in sentences (a ban's reason, an unverified address, a rate
 * limit), so its message is passed through rather than flattened into one of ours.
 */
export function nativeGoogleSignInMessage(error: unknown): string | null {
  if (error instanceof NativeGoogleAuthError) {
    switch (error.code) {
      case 'USER_CANCELLED':
        return null;
      case 'NO_ID_TOKEN':
        // Almost always the Android OAuth client: a missing package name or the wrong signing
        // SHA-1 makes the picker succeed and hand back nothing. Say so rather than "try again".
        return 'Google signed you in but returned no identity token. Please report this.';
      default:
        return 'Google sign-in could not start on this device. Please try again.';
    }
  }

  const message = (error as { message?: unknown } | null)?.message;
  if (typeof message === 'string' && message.trim() !== '') return message;
  return 'Google sign-in did not finish. Please try again.';
}
